import { computed, onScopeDispose, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from '@/services/api'
import { i18n } from '@/i18n'
import { normalizeAppearance, panelColors, rgba } from '@/utils/appearance'
import type { AppearanceSettings, LocaleCode, ThemeMode } from '@/types/monitoring'

const STORAGE_KEY = 'pulsewatch.appearance'
function localSettings(): AppearanceSettings {
  try {
    return normalizeAppearance(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'))
  } catch {
    return normalizeAppearance({})
  }
}

export const useAppearanceStore = defineStore('appearance', () => {
  const settings = ref<AppearanceSettings>(localSettings())
  const previewSettings = ref<AppearanceSettings | null>(null)
  const effective = computed(() => previewSettings.value ?? settings.value)
  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const systemDark = ref(media.matches)
  const initialized = ref(false)
  let pending: Promise<void> | null = null
  const isDark = computed(
    () =>
      effective.value.theme === 'dark' || (effective.value.theme === 'system' && systemDark.value),
  )

  function apply(persist = false): void {
    const value = effective.value
    const root = document.documentElement
    const colors = panelColors(value, isDark.value)
    root.classList.toggle('dark', isDark.value)
    root.classList.toggle('glass-cards', value.cardGlass)
    root.lang = value.locale
    i18n.global.locale.value = value.locale
    root.style.setProperty(
      '--canvas-color',
      isDark.value && value.backgroundColor === '#ffffff' ? '#111827' : value.backgroundColor,
    )
    root.style.setProperty(
      '--panel-gradient',
      `linear-gradient(${value.cardGradientAngle}deg,${rgba(colors.start, value.cardOpacity)},${rgba(colors.end, value.cardOpacity)})`,
    )
    root.style.setProperty('--panel-blur', `${value.cardBlur}px`)
    root.style.setProperty('--panel-ink', colors.ink)
    root.style.setProperty('--panel-muted', colors.muted)
    if (persist) {
      // Full/disabled storage must never prevent opening a settings page.
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value))
      } catch {
        /* Appearance remains usable without storage. */
      }
    }
    let favicon = document.querySelector<HTMLLinkElement>('link[data-pulsewatch-favicon]')
    if (value.faviconUrl) {
      if (!favicon) {
        favicon = document.createElement('link')
        favicon.rel = 'icon'
        favicon.dataset.pulsewatchFavicon = 'true'
        document.head.appendChild(favicon)
      }
      favicon.href = value.faviconUrl
    } else favicon?.remove()
  }

  async function initialize(token: string, authenticated = Boolean(token)): Promise<void> {
    apply()
    if (!authenticated || initialized.value) return
    if (pending) return pending
    pending = (async () => {
      settings.value = normalizeAppearance(await api.appearance(token))
      initialized.value = true
      apply(true)
    })().finally(() => {
      pending = null
    })
    return pending
  }
  async function save(token: string, value: AppearanceSettings): Promise<void> {
    settings.value = normalizeAppearance(
      await api.updateAppearance(token, normalizeAppearance(value)),
    )
    previewSettings.value = null
    apply(true)
  }
  function preview(value: AppearanceSettings | null): void {
    previewSettings.value = value ? normalizeAppearance(value) : null
    apply()
  }
  function setTheme(theme: ThemeMode): void {
    settings.value = { ...settings.value, theme }
    apply(true)
  }
  function setLocale(locale: LocaleCode): void {
    settings.value = { ...settings.value, locale }
    apply(true)
  }
  function mediaChanged(event: MediaQueryListEvent): void {
    systemDark.value = event.matches
    apply()
  }
  media.addEventListener('change', mediaChanged)
  onScopeDispose(() => media.removeEventListener('change', mediaChanged))
  apply()
  return {
    settings,
    effective,
    initialized,
    isDark,
    initialize,
    save,
    preview,
    setTheme,
    setLocale,
  }
})
